import React, { lazy } from 'react';

import { ForbiddenView } from '@/views/util/forbidden/Forbidden.view.tsx';

import { BreadcrumbDefinition, RouteEnum, RouteModel } from './Route.model';

const route = (
  path: RouteEnum,
  load: () => Promise<{ default: React.FC }>,
  breadcrumbs: BreadcrumbDefinition[] = [{ label: path, to: path }],
): RouteModel => ({
  component: lazy(load),
  handle: { breadcrumbs },
  load,
  path,
});

const eagerRoute = (
  path: RouteEnum,
  component: React.FC,
  breadcrumbs: BreadcrumbDefinition[] = [{ label: path, to: path }],
): RouteModel => ({ component, handle: { breadcrumbs }, path });

const detailsBreadcrumbs = (parent: RouteEnum): BreadcrumbDefinition[] => [
  { label: parent, to: parent },
  { label: 'details' },
];

export const ROUTES: RouteModel[] = [
  route(RouteEnum.ROOT, () => import('@/views/dashboard/Dashboard.view'), []),
  route(RouteEnum.DASHBOARD, () => import('@/views/dashboard/Dashboard.view')),
  route(RouteEnum.EMAILS, () => import('@/views/emails/Emails.view')),
  route(
    RouteEnum.FAILED_WORKFLOW_ACTIONS,
    () => import('@/views/failedWorkflowActions/FailedWorkflowActions.view'),
  ),
  route(RouteEnum.CHATS, () => import('@/views/chats/Chats.view.tsx')),
  route(
    RouteEnum.COMMISSION_CONFIGURATION,
    () => import('@/views/commissionConfiguration/CommissionConfiguration.view.tsx'),
  ),
  route(
    RouteEnum.COMMISSION_ADMINISTRATION,
    () => import('@/views/commissionAdministration/CommissionAdministration.view.tsx'),
  ),
  route(RouteEnum.COMMISSIONS, () => import('@/views/commissions/Commissions.view.tsx')),
  route(RouteEnum.CONTACTS, () => import('@/views/contacts/Contacts.view')),
  route(RouteEnum.CONTACTS_CREATE, () => import('@/views/contactsCreate/ContactsCreate.view'), [
    { label: RouteEnum.CONTACTS, to: RouteEnum.CONTACTS },
    { label: 'details' },
  ]),
  route(
    RouteEnum.CONTACTS_DETAILS,
    () => import('@/views/contactsDetails/ContactDetails.view'),
    detailsBreadcrumbs(RouteEnum.CONTACTS),
  ),
  route(
    RouteEnum.EMERGENCY_CONTACTS,
    () => import('@/views/emergencyContacts/list/EmergencyContactsList.view'),
  ),
  route(
    RouteEnum.EMERGENCY_CONTACTS_DETAILS,
    () => import('@/views/emergencyContacts/details/EmergencyContactsDetails.view'),
    detailsBreadcrumbs(RouteEnum.EMERGENCY_CONTACTS),
  ),
  route(
    RouteEnum.EMERGENCY_CONTACTS_EDIT,
    () => import('@/views/emergencyContacts/edit/EmergencyContactsEdit.view'),
    detailsBreadcrumbs(RouteEnum.EMERGENCY_CONTACTS),
  ),
  route(
    RouteEnum.CONTACTS_EDIT,
    () => import('@/views/contactsEdit/ContactsEdit.view'),
    detailsBreadcrumbs(RouteEnum.CONTACTS),
  ),
  route(RouteEnum.CUSTOMERS, () => import('@/views/customers/list/Customers.view')),
  route(
    RouteEnum.CUSTOMERS_CREATE_INDIVIDUAL,
    () => import('@/views/customers/createIndividual/CustomersCreateIndividual.view'),
    detailsBreadcrumbs(RouteEnum.CUSTOMERS),
  ),
  route(RouteEnum.CARS, () => import('@/views/cars/list/CarsList.view.tsx')),
  route(
    RouteEnum.CARS_DETAILS,
    () => import('@/views/cars/details/CarDetails.view.tsx'),
    detailsBreadcrumbs(RouteEnum.CARS),
  ),
  route(
    RouteEnum.EMERGENCY_CONTACTS_CREATE,
    () => import('@/views/emergencyContacts/create/EmergencyContactsCreate.view'),
    detailsBreadcrumbs(RouteEnum.EMERGENCY_CONTACTS),
  ),
  route(
    RouteEnum.CUSTOMERS_CREATE_BUSINESS,
    () => import('@/views/customers/createBusiness/CustomersCreateBusiness.view'),
    detailsBreadcrumbs(RouteEnum.CUSTOMERS),
  ),
  route(
    RouteEnum.CUSTOMERS_CREATE_FOREIGN,
    () => import('@/views/customers/createForeign/CustomersCreateForeign.view'),
    detailsBreadcrumbs(RouteEnum.CUSTOMERS),
  ),
  route(
    RouteEnum.CUSTOMERS_DETAILS,
    () => import('@/views/customerDetails/CustomerDetails.view'),
    detailsBreadcrumbs(RouteEnum.CUSTOMERS),
  ),
  route(
    RouteEnum.CUSTOMERS_EDIT,
    () => import('@/views/customerEdit/CustomerEdit.view'),
    detailsBreadcrumbs(RouteEnum.CUSTOMERS),
  ),
  route(RouteEnum.PROCESSES, () => import('@/views/processes/list/ProcessesList.view.tsx')),
  route(
    RouteEnum.PROCESSES_DETAILS,
    () => import('@/views/processesDetails/ProcessDetails.view'),
    detailsBreadcrumbs(RouteEnum.PROCESSES),
  ),
  route(RouteEnum.TASKS, () => import('@/views/tasks/list/TasksList.view')),
  route(
    RouteEnum.TASK_DETAILS,
    () => import('@/views/tasks/details/TaskDetails.view'),
    detailsBreadcrumbs(RouteEnum.TASKS),
  ),
  route(RouteEnum.DICTIONARIES, () => import('@/views/dictionaries/list/DictionariesList.view')),
  route(RouteEnum.REPORTS, () => import('@/views/reports/Reports.view')),
  route(
    RouteEnum.RECURRING_RESERVATIONS,
    () => import('@/views/recurringReservations/list/RecurringReservationsList.view'),
  ),
  route(
    RouteEnum.RECURRING_RESERVATION_DETAILS,
    () => import('@/views/recurringReservations/details/RecurringReservationDetails.view'),
    detailsBreadcrumbs(RouteEnum.RECURRING_RESERVATIONS),
  ),
  route(
    RouteEnum.RESOURCE_CALENDAR,
    () => import('@/views/resourceCalendar/ResourceCalendar.view'),
  ),
  route(RouteEnum.USERS, () => import('@/views/users/list/UsersList.view')),
  route(RouteEnum.ROLES, () => import('@/views/roles/Roles.view')),
  route(RouteEnum.SUPERVISORS, () => import('@/views/supervisors/Supervisors.view')),
  eagerRoute(RouteEnum.FORBIDDEN, ForbiddenView),
  route(
    RouteEnum.PROCESSES_DETAILS_OFFER_INSTANCE,
    () => import('@/views/processes/offerInstance/ProcessOfferInstance.view'),
    detailsBreadcrumbs(RouteEnum.PROCESSES),
  ),
  route(
    RouteEnum.COMPONENT_DEFINITIONS,
    () => import('@/views/componentDefinitions/list/ComponentDefinitionsList.view'),
  ),
  route(
    RouteEnum.OFFER_DEFINITIONS,
    () => import('@/views/offerDefinitions/list/OfferDefinitionsList.view'),
  ),
  route(
    RouteEnum.OFFER_DEFINITION_CREATE,
    () => import('@/views/offerDefinitions/details/OfferDefinitionDetails.view'),
    detailsBreadcrumbs(RouteEnum.OFFER_DEFINITIONS),
  ),
  route(
    RouteEnum.OFFER_DEFINITION_DETAILS,
    () => import('@/views/offerDefinitions/details/OfferDefinitionDetails.view'),
    detailsBreadcrumbs(RouteEnum.OFFER_DEFINITIONS),
  ),
  route(
    RouteEnum.COMPONENT_DEFINITION_DETAILS,
    () => import('@/views/componentDefinitions/details/ComponentDefinitionDetails.view'),
    detailsBreadcrumbs(RouteEnum.COMPONENT_DEFINITIONS),
  ),
  route(RouteEnum.SUPPLIERS, () => import('@/views/suppliers/list/SuppliersList.view')),
  route(
    RouteEnum.SUPPLIERS_DETAILS,
    () => import('@/views/suppliers/details/SupplierDetails.view'),
    detailsBreadcrumbs(RouteEnum.SUPPLIERS),
  ),
];
